import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { BrandMissionCollabRow } from "@/types/missions"

/**
 * Brand-side per-row state pill.
 *
 * Mirrors the creator-side `StateBadge` tone palette in posture but reads
 * off `BrandMissionCollabRow.state` instead of the creator-card
 * `Collab.state`. Two state-pill components exist (one per surface)
 * because the brand-row type lacks the creator-side `cta` / `submission`
 * fields and inlining the full `Collab` shape into the brand serializer
 * would bloat the wire.
 *
 * OKS-32 introduces the Drop alphabet (UX-Spec-OKS-32 §2.2):
 *   - applied   → sky    ("Applied" — distinct from "Accepted" blue)
 *   - rejected  → muted with strikethrough ("Rejected" — Drop terminal)
 *
 * OKS-33 adds the 12th state for Promotional (`purchased`).
 *
 * OKS-210 — Promotional palette realigned to UX-Spec-OKS-33 §2.
 *
 *   The OKS-32 Drop palette had been carried over to Promotional's
 *   shipping-arc states (`to_ship/shipped/delivered/posted`) at OKS-33
 *   merge, which drifted from the binding spec. This polish restores
 *   the Spec §2 hue progression for the BRAND-SIDE pill only:
 *
 *     | State       | Tone key       | Light          | Dark           |
 *     | ----------- | -------------- | -------------- | -------------- |
 *     | purchased   | amber          | amber-100/900  | amber-900/30   |
 *     | to_ship     | amber_darker   | amber-300/950  | amber-800/40   |
 *     | shipped     | blue           | blue-100/900   | blue-900/30    |
 *     | delivered   | blue_darker    | blue-300/950   | blue-800/40    |
 *     | posted      | purple         | purple-100/900 | purple-900/30  |
 *
 *   `purchased` keeps the lighter amber so it reads as a "before
 *   dispatch" sibling of `to_ship` (amber-darker = "ready to ship")
 *   rather than a single conflated bucket. Item #2 of the OKS-210
 *   follow-up explicitly reverses the OKS-33 collision.
 *
 *   INTENTIONAL ASYMMETRY: the creator-side `state-badge.tsx` keeps
 *   the Drop palette (`indigo / teal / orange`). UX-Spec-OKS-32 §2.2
 *   binds the creator surface; UX-Spec-OKS-33 §2 binds the brand
 *   surface — they diverge by design. Do NOT "re-fix" the creator
 *   side from this file.
 *
 *   INTENTIONAL COLLISION: `accepted` and `shipped` both map to the
 *   light-blue tone. Per PRD-OKS-210 §7 Q2, these states never co-
 *   occur on a single collab row and the i18n label disambiguates;
 *   reusing the hue is a conscious choice. If a future surface (e.g.
 *   a multi-state legend) ever needs them side-by-side, that ticket
 *   introduces a third blue rung — this is NOT to be unilaterally
 *   "fixed" here.
 */

type Tone =
  | "gray"
  | "blue"
  | "orange"
  | "green"
  | "destructive"
  | "sky"
  | "amber"
  | "indigo"
  | "teal"
  | "muted_strikethrough"
  // OKS-210 — Promotional realignment (UX-Spec-OKS-33 §2).
  | "amber_darker"
  | "blue_darker"
  | "purple"

const TONE_CLASSES: Record<Tone, string> = {
  gray: "bg-muted text-muted-foreground border-transparent",
  blue: "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100 border-transparent",
  green:
    "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-100 border-transparent",
  orange:
    "bg-orange-100 text-orange-900 dark:bg-orange-900/30 dark:text-orange-100 border-transparent",
  destructive:
    "bg-destructive/15 text-foreground dark:bg-destructive/30 dark:text-white border-transparent",
  // OKS-32 — Drop tones (UX Spec §2.2).
  sky: "bg-sky-100 text-sky-900 dark:bg-sky-900/30 dark:text-sky-100 border-transparent",
  amber:
    "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100 border-transparent",
  indigo:
    "bg-indigo-100 text-indigo-900 dark:bg-indigo-900/30 dark:text-indigo-100 border-transparent",
  teal: "bg-teal-100 text-teal-900 dark:bg-teal-900/30 dark:text-teal-100 border-transparent",
  muted_strikethrough:
    "bg-muted text-muted-foreground line-through border-transparent",
  // OKS-210 — Promotional realignment (UX-Spec-OKS-33 §2). WCAG AA verified
  // on each light/dark pair (≥ 4.5:1) against Tailwind v4 ramps. Dark-mode
  // opacity bumped to /40 on the _darker tones to stay visually distinct
  // from their lighter siblings in dark mode.
  amber_darker:
    "bg-amber-300 text-amber-950 dark:bg-amber-800/40 dark:text-amber-100 border-transparent",
  blue_darker:
    "bg-blue-300 text-blue-950 dark:bg-blue-800/40 dark:text-blue-100 border-transparent",
  purple:
    "bg-purple-100 text-purple-900 dark:bg-purple-900/30 dark:text-purple-100 border-transparent",
}

const STATE_TO_TONE: Record<BrandMissionCollabRow["state"], Tone> = {
  assigned: "gray",
  applied: "sky",
  // INTENTIONAL hue collision with `shipped` below — non-co-occurring on
  // a single collab; label disambiguates. PRD-OKS-210 §7 Q2.
  accepted: "blue",
  rejected: "muted_strikethrough",
  // OKS-210 — `purchased` keeps the lighter amber as the "before dispatch"
  // sibling; `to_ship` moves to amber_darker as the "ready to ship"
  // sibling. Reverses the OKS-33 collision (Item #2 of the follow-up).
  purchased: "amber",
  to_ship: "amber_darker",
  // OKS-210 — Drop tones replaced per UX-Spec-OKS-33 §2.
  // Drop palette (indigo / teal / orange) is retained on the creator-
  // side `state-badge.tsx`; do NOT re-align that file from here.
  shipped: "blue",
  delivered: "blue_darker",
  posted: "purple",
  completed: "green",
  declined: "gray",
  issue: "destructive",
  // OKS-483 — deadline-lapse lifecycle. `expired` neutral grey (terminal);
  // `reopen_requested` blue interim.
  expired: "gray",
  reopen_requested: "blue",
}

interface CollabStatePillProps {
  state: BrandMissionCollabRow["state"]
  className?: string
}

export function CollabStatePill({ state, className }: CollabStatePillProps) {
  const { t } = useTranslation()
  const tone = STATE_TO_TONE[state]
  return (
    <Badge
      data-testid="collab-state-pill"
      data-state={state}
      data-tone={tone}
      className={cn(TONE_CLASSES[tone], className)}
    >
      {t(`collabs.state_pill.${state}`)}
    </Badge>
  )
}
